import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { type Key } from "ts-key-enum";

const emptyDefault: Key[] = [];

export function useKeyPress<T>(
    preventDefault: Key[] = emptyDefault,
): [MutableRefObject<T | null>, Record<Key, boolean>] {
    const fieldRef = useRef<T | null>(null);
    const [keysPressed, setKeysPressed] = useState<Record<Key, boolean>>(
        {} as Record<Key, boolean>,
    );

    const downHandler = useCallback(
        (e: KeyboardEvent) => {
            if (e.repeat) {
                // Holding a key down forces the component to keep rerending, which causes performance issues
                return;
            }

            const keyPressed = e.key as Key;
            if (preventDefault.includes(keyPressed)) {
                e.preventDefault();
            }
            setKeysPressed((prev) => ({
                ...prev,
                [keyPressed]: true,
            }));
        },
        [preventDefault],
    );

    const upHandler = useCallback(
        (e: KeyboardEvent) => {
            const keyReleased = e.key as Key;
            if (preventDefault.includes(keyReleased)) {
                e.preventDefault();
            }
            setKeysPressed((prev) => {
                const newKeysPressed = { ...prev };
                delete newKeysPressed[keyReleased];
                return newKeysPressed;
            });
        },
        [preventDefault],
    );

    const clearHandler = useCallback(() => {
        setKeysPressed({} as Record<Key, boolean>);
    }, []);

    useEffect(() => {
        const current = fieldRef.current as HTMLElement | null;
        current?.addEventListener("keydown", downHandler);
        current?.addEventListener("keyup", upHandler);

        // When we focus away from this ref, we want to clear keys so they aren't "pressed" when refocused
        current?.addEventListener("blur", clearHandler);
        current?.addEventListener("click", clearHandler);

        return () => {
            current?.removeEventListener("keydown", downHandler);
            current?.removeEventListener("keyup", upHandler);
            current?.removeEventListener("blur", clearHandler);
            current?.removeEventListener("click", clearHandler);
        };
    }, [upHandler, downHandler, clearHandler]);

    return [fieldRef, keysPressed];
}
